import { useState, useEffect, useCallback } from 'react';
import logger from '../services/logger';
import {
  getCurrentUser,
  subscribeToAuthState,
  login as authLogin,
  register as authRegister,
  logout as authLogout,
  type User,
} from '../services/graphql/authService';

export type { User };

/**
 * Hook to handle GraphQL authentication
 * Provides the current user, loading state, and auth functions
 *
 * @returns Object containing the authenticated user, loading state, and auth functions
 */
export const useGraphQLAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial user
    const fetchUser = async () => {
      try {
        setLoading(true);
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        logger.debug('No authenticated user found');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();

    // Subscribe to auth state changes
    const unsubscribe = subscribeToAuthState((newUser) => {
      setUser(newUser);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (
      email: string,
      password: string
    ): Promise<{ user: User | null; error: Error | null }> => {
      setLoading(true);
      const result = await authLogin(email, password);
      setLoading(false);
      return result;
    },
    []
  );

  /**
   * Register a new user
   */
  const register = useCallback(
    async (
      email: string,
      password: string,
      fullName: string,
      phoneNumber?: string
    ): Promise<{ user: User | null; error: Error | null }> => {
      setLoading(true);
      const result = await authRegister(email, password, fullName, phoneNumber);
      setLoading(false);
      return result;
    },
    []
  );

  /**
   * Logout the current user
   */
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true);
    await authLogout();
    setLoading(false);
  }, []);

  return {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };
};

export default useGraphQLAuth;
